import {
  Buffer,
  BufferUsage,
  Container,
  Geometry,
  Mesh,
  RenderTexture,
  Shader,
  type Renderer as PixiRenderer,
  type UniformGroup,
} from 'pixi.js';
import { VISUALS } from '../config.ts';

/**
 * Format for the scene and bloom buffers when the GPU can render into half-float.
 *
 * rgba16float, not the packed rg11b10ufloat that engines usually reach for. Two reasons,
 * both specific to this board. Availability: the packed format needs
 * EXT_color_buffer_float, while the 16F formats are also covered by
 * EXT_color_buffer_half_float, so this runs on strictly more devices. And precision: the
 * packed format gives blue a five-bit mantissa, which is a 3% step — coarser than 8-bit
 * sRGB is in the midtones — and this game is a dark blue field covered in hairline
 * contours, which is the worst possible content for that. Ten bits of mantissa is better
 * than 8-bit everywhere, and the extra four bytes a pixel only land on one full-screen
 * buffer.
 */
const HDR_FORMAT = 'rgba16float' as const;

/**
 * Post-processing.
 *
 * The scene is rendered into a texture instead of straight to the screen, and then put
 * through a short chain: a bright pass, a separable blur at quarter resolution, and a
 * composite that adds the bloom back, tonemaps, grades, and lays on vignette, grain and a
 * touch of chromatic aberration.
 *
 * Bloom is the one that matters. Every highlight in this game — the fluid's specular, the
 * magnet halos, the goal ring — is a small bright feature on a very dark board, which is
 * exactly the case where bleeding light into the surroundings reads as physical rather
 * than as an effect. The rest is there to stop the image looking like unprocessed
 * framebuffer: the tonemap rolls highlights off instead of clipping them flat, the grade
 * splits shadows cool and highlights warm, and the grain gives the dark areas something to
 * sit on so they do not band.
 *
 * The whole chain is four extra passes, three of them at a sixteenth of the pixels.
 */

export type PostQuality = 'off' | 'low' | 'high';

/**
 * Fraction of the viewport the bloom buffers run at, per tier.
 *
 * This is the only knob that meaningfully changes what the chain costs — the composite is
 * one full-screen pass either way, so a "low" setting that only skipped a couple of taps
 * would be a label rather than a saving.
 */
const BLOOM_SCALE: Record<Exclude<PostQuality, 'off'>, number> = {
  high: 0.25,
  low: 0.125,
};

const quadVertex = /* glsl */ `#version 300 es
in vec2 aPosition;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;

out vec2 vUV;

void main() {
    mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
    gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
    // The quad is a unit square scaled to its target, so position is already UV.
    vUV = aPosition;
}
`;

const brightFragment = /* glsl */ `#version 300 es
precision mediump float;
// ES 3.00 defaults fragment samplers to lowp, whose range is (-2, 2). Reading an HDR
// scene buffer through that would clamp away exactly the headroom this chain exists to
// recover, so every sampler in the chain is qualified explicitly.
precision mediump sampler2D;

in vec2 vUV;
out vec4 fragColor;

uniform sampler2D uScene;
uniform vec2  uTexel;
uniform float uThreshold;
uniform float uKnee;

/**
 * Karis weight: one over one plus luma.
 *
 * A plain average of four taps lets a single very bright pixel carry the whole block, and
 * once the scene buffer is HDR those pixels are no longer capped at 1.0 — a specular
 * glint that moves half a pixel makes the bloom around it flicker. Weighting each tap by
 * its inverse luma averages in *perceived* brightness instead, which is stable.
 */
float karisWeight(vec3 c) {
    return 1.0 / (1.0 + dot(c, vec3(0.2126, 0.7152, 0.0722)));
}

/** Four bilinear taps: a box downsample, so quarter-res bloom does not sparkle. */
vec3 sampleBox(vec2 uv) {
    vec3 a = texture(uScene, uv + vec2(-uTexel.x, -uTexel.y)).rgb;
    vec3 b = texture(uScene, uv + vec2( uTexel.x, -uTexel.y)).rgb;
    vec3 c = texture(uScene, uv + vec2(-uTexel.x,  uTexel.y)).rgb;
    vec3 d = texture(uScene, uv + vec2( uTexel.x,  uTexel.y)).rgb;
    float wa = karisWeight(a);
    float wb = karisWeight(b);
    float wc = karisWeight(c);
    float wd = karisWeight(d);
    return (a * wa + b * wb + c * wc + d * wd) / max(wa + wb + wc + wd, 1e-4);
}

void main() {
    vec3 color = sampleBox(vUV);
    float luminance = dot(color, vec3(0.2126, 0.7152, 0.0722));
    // Soft knee: fade the contribution in around the threshold rather than switching it
    // on, so a highlight drifting past it does not pop.
    float contribution = smoothstep(uThreshold, uThreshold + uKnee, luminance);
    fragColor = vec4(color * contribution, 1.0);
}
`;

const blurFragment = /* glsl */ `#version 300 es
precision mediump float;
precision mediump sampler2D;

in vec2 vUV;
out vec4 fragColor;

uniform sampler2D uSource;
uniform vec2 uDirection;

void main() {
    vec2 offset1 = uDirection * 1.3846153846;
    vec2 offset2 = uDirection * 3.2307692308;

    vec3 sum = texture(uSource, vUV).rgb * 0.2270270270;
    sum += texture(uSource, vUV + offset1).rgb * 0.3162162162;
    sum += texture(uSource, vUV - offset1).rgb * 0.3162162162;
    sum += texture(uSource, vUV + offset2).rgb * 0.0702702703;
    sum += texture(uSource, vUV - offset2).rgb * 0.0702702703;

    fragColor = vec4(sum, 1.0);
}
`;

const compositeFragment = /* glsl */ `#version 300 es
precision highp float;
// The composite reads the HDR scene buffer directly, before the tonemap has brought it
// back into range, so this one needs the full range rather than mediump's.
precision highp sampler2D;

in vec2 vUV;
out vec4 fragColor;

uniform sampler2D uScene;
uniform sampler2D uBloom;
uniform float uBloomIntensity;
uniform float uAberration;
uniform float uVignette;
uniform float uGrain;
uniform float uTime;
uniform float uExposure;
uniform float uBlackPoint;
uniform float uContrast;
uniform vec3  uFlash;

/**
 * Where the contrast S-curve pivots. Low, so the curve steepens the midtones and
 * highlights without subtracting the darkest values straight through zero.
 */
const float CONTRAST_PIVOT = 0.18;

/** Narkowicz's ACES approximation: a filmic highlight rolloff in six multiplies. */
vec3 tonemap(vec3 x) {
    const float a = 2.51;
    const float b = 0.03;
    const float c = 2.43;
    const float d = 0.59;
    const float e = 0.14;
    return clamp((x * (a * x + b)) / (x * (c * x + d) + e), 0.0, 1.0);
}

float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

void main() {
    vec2 centered = vUV - 0.5;
    float radius = dot(centered, centered);

    // Chromatic aberration, growing towards the edge of the frame the way a real lens
    // does. Sampled per channel off the scene, so it costs two extra taps.
    vec2 shift = centered * uAberration * radius;
    vec3 scene;
    scene.r = texture(uScene, vUV + shift).r;
    scene.g = texture(uScene, vUV).g;
    scene.b = texture(uScene, vUV - shift).b;

    vec3 bloom = texture(uBloom, vUV).rgb;
    vec3 color = scene + bloom * uBloomIntensity;

    color += uFlash;
    // Bloom adds light everywhere it touches, which lifts the blacks off zero and leaves
    // the board looking hazy. Put them back before the tonemap.
    color = max(color - uBlackPoint, 0.0);
    color = tonemap(color * uExposure);
    // Contrast, pivoted near the shadow end rather than at mid-grey.
    //
    // Pivoting at 0.5 is what every tutorial writes, and on this image it was catastrophic:
    // the operation subtracts (pivot - pivot/contrast) from everything below the pivot, so
    // at 1.07 every displayed value under 0.033 mapped negative and clamped to black. The
    // table is authored at (0.045, 0.052, 0.074) and arrives at the tonemap around 0.023 —
    // it has never once been drawn. What the board actually showed was wireframe on void,
    // with wall shadows at 4/255 against a 1/255 floor. Pivoting low keeps the highlight
    // contrast this is here for and gives the bottom of the range back.
    color = clamp((color - CONTRAST_PIVOT) * uContrast + CONTRAST_PIVOT, 0.0, 1.0);

    // Grade: cool the shadows, warm the highlights. Cheap split tone, applied after the
    // tonemap so it works on displayed values rather than scene values.
    float luminance = dot(color, vec3(0.2126, 0.7152, 0.0722));
    vec3 shadowTint = vec3(0.86, 0.94, 1.12);
    vec3 highlightTint = vec3(1.06, 1.0, 0.94);
    color *= mix(shadowTint, highlightTint, smoothstep(0.15, 0.75, luminance));

    color *= 1.0 - uVignette * radius * 2.4;

    // Grain, weighted towards the shadows where banding would otherwise show.
    float noise = hash(vUV * 512.0 + fract(uTime) * 91.7) - 0.5;
    color += noise * uGrain * (1.0 - luminance * 0.7);

    fragColor = vec4(max(color, 0.0), 1.0);
}
`;

interface Pass {
  container: Container;
  mesh: Mesh<Geometry, Shader>;
  uniforms: UniformGroup;
}

export class PostProcess {
  quality: PostQuality;

  /** Additive full-screen flash, decayed every frame. */
  private readonly flash = new Float32Array(3);

  private sceneTexture!: RenderTexture;
  private bloomA!: RenderTexture;
  private bloomB!: RenderTexture;

  private bright!: Pass;
  private blurHorizontal!: Pass;
  private blurVertical!: Pass;
  private composite!: Pass;

  /**
   * Whether this GPU can render into half-float at all. Set once from the renderer's
   * extensions; the chain runs unchanged in 8-bit when it cannot.
   */
  hdrCapable = false;

  private width = 1;
  private height = 1;
  private resolution = 1;
  private elapsed = 0;
  /** Tier the current buffers were allocated for. */
  private allocatedFor: PostQuality = 'off';

  constructor(quality: PostQuality = 'high') {
    this.quality = quality;
  }

  get enabled(): boolean {
    return this.quality !== 'off';
  }

  /**
   * Declare half-float render-target support. Call before the first resize; changing it
   * later reallocates the chain.
   */
  setHdr(supported: boolean): void {
    if (supported === this.hdrCapable) return;
    this.hdrCapable = supported;
    // Force the next resize to reallocate rather than short-circuit on unchanged size.
    this.allocatedFor = 'off';
    this.width = -1;
  }

  /**
   * Half-float doubles what the scene buffer costs to write and to read back in the
   * composite, and that is bandwidth, which is the thing a weak mobile GPU has least of.
   * So it rides with the quality tier: the low tier — which is already the manual escape
   * hatch, at an eighth-resolution bloom — stays 8-bit.
   */
  get usesHdr(): boolean {
    return this.hdrCapable && this.quality === 'high';
  }

  /** (Re)allocate the chain for a viewport size in CSS pixels. */
  resize(width: number, height: number, resolution: number): void {
    if (
      width === this.width &&
      height === this.height &&
      resolution === this.resolution &&
      this.quality === this.allocatedFor
    ) {
      return;
    }
    this.width = width;
    this.height = height;
    this.resolution = resolution;
    this.allocatedFor = this.quality;

    this.destroyTextures();

    // Omitted entirely rather than spelled 'rgba8unorm' when there is no float support:
    // Pixi picks its own default, which is what every other texture in the app gets.
    const hdrFormat = this.usesHdr ? { format: HDR_FORMAT } : {};

    this.sceneTexture = RenderTexture.create({
      width,
      height,
      resolution,
      antialias: false,
      scaleMode: 'linear',
      ...hdrFormat,
    });

    const scale = BLOOM_SCALE[this.quality === 'low' ? 'low' : 'high'];
    const bloomWidth = Math.max(8, Math.round(width * scale));
    const bloomHeight = Math.max(8, Math.round(height * scale));
    const bloomOptions = {
      width: bloomWidth,
      height: bloomHeight,
      resolution,
      antialias: false,
      scaleMode: 'linear',
      ...hdrFormat,
    } as const;
    this.bloomA = RenderTexture.create(bloomOptions);
    this.bloomB = RenderTexture.create(bloomOptions);

    this.bright = this.createPass(brightFragment, bloomWidth, bloomHeight, {
      uScene: this.sceneTexture,
      uniforms: {
        // One *device* texel of the scene buffer. In CSS pixels this was off by the
        // device pixel ratio, which on a phone blurred the bright pass twice as wide as
        // the box downsample it is supposed to be.
        uTexel: {
          value: new Float32Array([1 / (width * resolution), 1 / (height * resolution)]),
          type: 'vec2<f32>',
        },
        uThreshold: { value: VISUALS.bloomThreshold, type: 'f32' },
        uKnee: { value: VISUALS.bloomKnee, type: 'f32' },
      },
    });

    this.blurHorizontal = this.createPass(blurFragment, bloomWidth, bloomHeight, {
      uSource: this.bloomA,
      uniforms: {
        uDirection: { value: new Float32Array([1.6 / bloomWidth, 0]), type: 'vec2<f32>' },
      },
    });

    this.blurVertical = this.createPass(blurFragment, bloomWidth, bloomHeight, {
      uSource: this.bloomB,
      uniforms: {
        uDirection: { value: new Float32Array([0, 1.6 / bloomHeight]), type: 'vec2<f32>' },
      },
    });

    this.composite = this.createPass(compositeFragment, width, height, {
      uScene: this.sceneTexture,
      uBloom: this.bloomA,
      uniforms: {
        uBloomIntensity: { value: 1.0, type: 'f32' },
        uAberration: { value: 0.006, type: 'f32' },
        uVignette: { value: 0.28, type: 'f32' },
        uGrain: { value: 0.028, type: 'f32' },
        uTime: { value: 0, type: 'f32' },
        uExposure: { value: VISUALS.exposure, type: 'f32' },
        uBlackPoint: { value: VISUALS.blackPoint, type: 'f32' },
        uContrast: { value: VISUALS.contrast, type: 'f32' },
        uFlash: { value: new Float32Array(3), type: 'vec3<f32>' },
      },
    });
  }

  private createPass(
    fragment: string,
    targetWidth: number,
    targetHeight: number,
    resources: { uniforms: Record<string, unknown> } & Record<string, unknown>,
  ): Pass {
    const { uniforms, ...textures } = resources;

    const shaderResources: Record<string, unknown> = { postUniforms: uniforms };
    for (const [name, texture] of Object.entries(textures)) {
      shaderResources[name] = (texture as RenderTexture).source;
    }
    // One sampler is enough: every source in the chain uses linear clamped filtering.
    const first = Object.values(textures)[0] as RenderTexture;
    shaderResources.uSampler = first.source.style;

    const shader = Shader.from({
      gl: { vertex: quadVertex, fragment },
      resources: shaderResources,
    });

    // Unit quad, scaled to the target. Position doubles as UV.
    const geometry = new Geometry({
      attributes: {
        aPosition: new Buffer({
          data: new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]),
          usage: BufferUsage.VERTEX,
        }),
      },
      indexBuffer: new Uint32Array([0, 1, 2, 0, 2, 3]),
    });

    const mesh = new Mesh({ geometry, shader, label: 'post' });
    const container = new Container({ label: 'post-pass' });
    container.scale.set(targetWidth, targetHeight);
    container.addChild(mesh);

    return { container, mesh, uniforms: shader.resources.postUniforms as UniformGroup };
  }

  /** Add an additive flash; decays on its own. */
  addFlash(r: number, g: number, b: number): void {
    this.flash[0] += r;
    this.flash[1] += g;
    this.flash[2] += b;
  }

  update(dt: number): void {
    this.elapsed += dt;
    const decay = Math.exp(-dt * 6);
    this.flash[0] *= decay;
    this.flash[1] *= decay;
    this.flash[2] *= decay;
  }

  /** Where the scene should be drawn this frame, or null to draw straight to screen. */
  get sceneTarget(): RenderTexture | null {
    return this.enabled ? this.sceneTexture : null;
  }

  /** Run the chain and put the result on screen. */
  present(renderer: PixiRenderer): void {
    if (!this.enabled) return;

    renderer.render({ container: this.bright.container, target: this.bloomA, clear: true });
    renderer.render({ container: this.blurHorizontal.container, target: this.bloomB, clear: true });
    renderer.render({ container: this.blurVertical.container, target: this.bloomA, clear: true });

    const uniforms = this.composite.uniforms.uniforms;
    uniforms.uTime = this.elapsed;
    uniforms.uBloomIntensity = VISUALS.bloomIntensity;
    // The cheap tier keeps bloom and grade but drops the per-channel scene taps.
    uniforms.uAberration = this.quality === 'low' ? 0 : VISUALS.chromaticAberration;
    uniforms.uVignette = VISUALS.vignette;
    uniforms.uGrain = VISUALS.grain;
    uniforms.uExposure = VISUALS.exposure;
    uniforms.uBlackPoint = VISUALS.blackPoint;
    uniforms.uContrast = VISUALS.contrast;

    const brightUniforms = this.bright.uniforms.uniforms;
    brightUniforms.uThreshold = VISUALS.bloomThreshold;
    brightUniforms.uKnee = VISUALS.bloomKnee;
    (uniforms.uFlash as Float32Array).set(this.flash);

    renderer.render({ container: this.composite.container });
  }

  private destroyTextures(): void {
    this.sceneTexture?.destroy(true);
    this.bloomA?.destroy(true);
    this.bloomB?.destroy(true);
  }

  destroy(): void {
    this.destroyTextures();
  }
}
